import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING } from "@/constants/landingTestIds";

const LOGO_URL = "/logo-icon.png";

const NAV_LINKS = [
  { label: "Story", href: "#story", testId: LANDING.navLinkStory },
  { label: "Features", href: "#features", testId: LANDING.navLinkFeatures },
  { label: "Pricing", href: "#pricing", testId: LANDING.navLinkPricing },
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(!isHome);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!isHome) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const scrollTo = (href) => {
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/${href}`);
    }
  };

  const goHome = () => {
    setMenuOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a
          href="/"
          data-testid={LANDING.navLogo}
          onClick={(e) => {
            e.preventDefault();
            goHome();
          }}
          className="flex items-center gap-2 shrink-0"
        >
          <img src={LOGO_URL} alt="Tracre One" className="h-9 w-9 object-contain drop-shadow-sm" />
          <span
            className={`font-heading text-lg font-bold tracking-tight transition-colors ${
              scrolled ? "text-[#0F172A]" : "text-white"
            }`}
          >
            Tracre One
          </span>
        </a>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={link.testId}
              onClick={() => scrollTo(link.href)}
              className={`text-sm font-medium transition-colors hover:text-[#2563EB] ${
                scrolled ? "text-slate-600" : "text-slate-200"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden md:block">
          <Button
            data-testid={LANDING.navDownloadBtn}
            onClick={() => scrollTo("#download")}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg px-5 h-11 font-semibold shadow-sm hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 gap-2"
          >
            <Smartphone className="w-4 h-4" />
            Download Android App
          </Button>
        </div>

        <button
          data-testid={LANDING.navMenuToggle}
          onClick={() => setMenuOpen((v) => !v)}
          className={`md:hidden p-2 rounded-lg ${scrolled ? "text-[#0F172A]" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 px-6 py-6 flex flex-col gap-5 animate-in fade-in slide-in-from-top-2 duration-200">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={`mobile-${link.testId}`}
              onClick={() => scrollTo(link.href)}
              className="text-left text-base font-medium text-slate-700"
            >
              {link.label}
            </button>
          ))}
          <Button
            onClick={() => scrollTo("#download")}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-11 font-semibold w-full"
          >
            Download Android App
          </Button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
